import { baixarArquivo, request, streamSse } from '@/api/client'

/**
 * Camada de dados REAL — cada função chama o endpoint documentado em
 * `_MAPA_API.md`.
 *
 * Espelha a forma de `preview.ts`, para que as telas não saibam qual das duas
 * está em uso. A escolha acontece em `index.ts`.
 *
 * **O que `request` já resolve** (`api/client.ts`), e por isso não é repetido aqui:
 * token, 401 que não desloga por falha do SISCOMEX, resposta HTML do nginx
 * traduzida em mensagem legível, tempo limite de 30 s e montagem da query.
 *
 * `body` é corpo JSON; `form` é multipart. Nunca os dois juntos.
 */

/** Teto de itens por página aceito pelo backend (`le=200` em todos os routers). */
const POR_PAGINA = 200

type Pagina<T> = { itens: T[]; total?: number; paginas?: number }

/**
 * Percorre TODAS as páginas de um endpoint paginado e devolve a lista inteira.
 *
 * As telas filtram, contam e paginam em memória, então receber só o primeiro
 * lote fazia registros simplesmente desaparecerem (ex.: empresa em posição
 * alfabética acima de 200 não aparecia na lista nem na busca). Para quando o
 * `pages`/`total` da resposta é alcançado ou, quando o endpoint não informa
 * nenhum dos dois, quando a página vem incompleta.
 */
async function todasPaginas<T>(buscar: (page: number) => Promise<Pagina<T>>): Promise<T[]> {
  const tudo: T[] = []
  for (let page = 1; ; page += 1) {
    const { itens, total, paginas } = await buscar(page)
    tudo.push(...itens)
    const fim =
      paginas != null
        ? page >= paginas
        : total != null
          ? tudo.length >= total
          : itens.length < POR_PAGINA
    if (fim || itens.length === 0) return tudo
  }
}

/** Lista completa de processos (usada pela tela e pelos alertas de vencimento). */
const listarProcessos = (tipo?: string, ano?: number) =>
  todasPaginas<unknown>(async (page) => {
    const r = await request<{ processos?: unknown[]; pages?: number; total?: number }>(
      '/api/processos',
      { query: { tipo, ano, page, limit: POR_PAGINA } },
    )
    return { itens: r?.processos ?? [], paginas: r?.pages, total: r?.total }
  })

/** Histórico completo (LPCOs e documentos): a rota devolve array puro. */
const listarHistorico = (rota: string) =>
  todasPaginas<unknown>(async (page) => {
    const r = await request<unknown>(rota, { query: { page, per_page: POR_PAGINA } })
    return { itens: Array.isArray(r) ? r : [] }
  })

// Os cadastros do financeiro respondem com hífen na URL (grupos-despesa, centros-custo).
const finRota = (recurso: string) => recurso.replace(/_/g, '-')

export const dadosReais = {
  /* ========================================================= DUIMP */

  consultarDuimp: (numero: string) =>
    request(`/api/duimp/consultar/${encodeURIComponent(numero)}`),

  rascunhos: () => request('/api/duimp/drafts'),

  duimpDraftGet: (id: number) => request(`/api/duimp/draft/${id}`),

  duimpDraftExcluir: (id: number) => request(`/api/duimp/draft/${id}`, { method: 'DELETE' }),

  duimpConsultaCompleta: (numero: string) =>
    request(`/api/duimp/retificacao/consultar/${encodeURIComponent(numero)}`, {
      timeout: 120_000,
    }),

  importadorLookup: (cnpj: string) =>
    request(`/api/duimp/importador-lookup/${encodeURIComponent(cnpj)}`),

  /** Operadores estrangeiros (fabricantes/exportadores) reais do importador no CATP. */
  duimpOperadoresEstrangeiros: (cnpj: string) =>
    request('/api/duimp/operadores-estrangeiros', { query: { cnpj }, timeout: 90_000 }),

  urfList: () => request('/api/duimp/urf-list', { timeout: 120_000 }),

  /** Incoterms aceitos na condição de venda do item. */
  duimpIncoterms: () => request('/api/duimp/referencia/incoterm'),

  /** Tabela de domínio do SISCOMEX (TABX): embalagem, pais, moeda, etc. */
  dominioTabela: (tabela: string) =>
    request(`/api/duimp/domains/${encodeURIComponent(tabela)}`, { timeout: 120_000 }),

  duimpDraftSalvar: (d: {
    title?: string
    form_data?: unknown
    items_data?: unknown[]
    draft_id?: number
    numero_processo?: string
    client_key?: string
  }) => {
    const form = new FormData()
    if (d.title) form.append('title', d.title)
    form.append('form_data', JSON.stringify(d.form_data ?? {}))
    form.append('items_data', JSON.stringify(d.items_data ?? []))
    if (d.numero_processo) form.append('numero_processo', d.numero_processo)
    if (d.client_key) form.append('client_key', d.client_key)
    if (d.draft_id) form.append('draft_id', String(d.draft_id))
    // Rascunho com muitos itens em hora de Portal lento passava dos 30 s
    // padrão: o navegador desistia e avisava erro mesmo com o rascunho salvo.
    return request('/api/duimp/draft', { method: 'POST', form, timeout: 120_000 })
  },

  /**
   * Pipeline de REGISTRO da DUIMP no SISCOMEX (mesmo contrato do sistema antigo).
   * `/diagnostico` cria/atualiza a DUIMP, salva os itens e roda o diagnóstico;
   * `/registrar` faz o mesmo e efetiva o registro. Escrita REAL no SISCOMEX.
   */
  duimpDiagnosticar: (form: unknown, itens: unknown[], draftId?: number) => {
    const fd = new FormData()
    fd.append('form_data', JSON.stringify(form ?? {}))
    fd.append('items_data', JSON.stringify(itens ?? []))
    if (draftId) fd.append('draft_id', String(draftId))
    return request('/api/duimp/diagnostico', { method: 'POST', form: fd, timeout: 180_000 })
  },

  duimpRegistrar: (form: unknown, itens: unknown[], draftId?: number) => {
    const fd = new FormData()
    fd.append('form_data', JSON.stringify(form ?? {}))
    fd.append('items_data', JSON.stringify(itens ?? []))
    if (draftId) fd.append('draft_id', String(draftId))
    return request('/api/duimp/registrar', { method: 'POST', form: fd, timeout: 180_000 })
  },

  historico: () => request('/api/duimp/history'),

  /** O histórico de retificação são as ações `retif_*` do mesmo endpoint. */
  retificacoes: () => request('/api/duimp/history'),

  tributacao: (ncm: string, pais?: number | string) =>
    request(`/api/duimp/tributacao/${encodeURIComponent(ncm)}`, {
      query: pais ? { pais: String(pais) } : undefined,
      timeout: 120_000,
    }),

  ncmAtributos: (ncm: string) =>
    request(`/api/duimp/ncm-atributos/${encodeURIComponent(ncm)}`, { timeout: 120_000 }),

  /**
   * Atributos de um fundamento legal OPCIONAL do TTCE (Cenário 2). Chamado
   * quando o fundamento escolhido tem `possuiAtributos === 'S'` — o SISCOMEX
   * devolve os atributos dinâmicos e seus domínios. Leitura (consulta TTCE).
   */
  tributacaoOpcional: (
    ncm: string,
    o: { pais?: number | string; tributo: number | string; regime: number | string; fundamento: number | string },
  ) =>
    request(`/api/duimp/tributacao-opcional/${encodeURIComponent(ncm)}`, {
      query: {
        pais: o.pais != null ? String(o.pais) : undefined,
        tributo: String(o.tributo),
        regime: String(o.regime),
        fundamento: String(o.fundamento),
      },
      timeout: 120_000,
    }),

  /**
   * Consulta a carga por CE/RUC no SISCOMEX (conhecimento, transporte, pesos,
   * volumes, frete, recinto). SOMENTE LEITURA: não cria nem altera DUIMP.
   */
  duimpCargoLookup: (tipo: string, numero: string, cnpj: string, conhecimento = '') =>
    request('/api/duimp/cargo-lookup', {
      method: 'POST',
      body: { tipo, numero, cnpj, conhecimento },
      timeout: 120_000,
    }),

  /**
   * 🐢 2 chamadas ao SISCOMEX e até 16 ao Banco Central. O tempo limite padrão de
   * 30 s não basta — daí os 90 s. Acima disso o Cloudflare corta em 100 s, então
   * o certo é virar job assíncrono no backend.
   */
  duimpCustos: (numero: string) =>
    request(`/api/processos/custos-duimp/${encodeURIComponent(numero)}`, { timeout: 90_000 }),

  duimpListar: (f: { cnpj?: string; data_inicial?: string; data_final?: string }) =>
    request('/api/duimp/listar', { query: f }),

  /* ==================================================== retificação */

  retifSalvar: (numero: string, versao: string, payload: unknown) =>
    request(
      `/api/duimp/retificacao/salvar/${encodeURIComponent(numero)}/${encodeURIComponent(versao)}`,
      { method: 'POST', body: payload, timeout: 120_000 },
    ),

  retifItens: (numero: string, versao: string, itens: unknown[], importadorNi: string) =>
    request(
      `/api/duimp/retificacao/itens/${encodeURIComponent(numero)}/${encodeURIComponent(versao)}`,
      { method: 'POST', body: { itens, importadorNi }, timeout: 120_000 },
    ),

  retifDiagnostico: (numero: string, versao: string, totalItens: number) => {
    const form = new FormData()
    form.append('total_itens', String(totalItens))
    return request(
      `/api/duimp/retificacao/diagnostico/${encodeURIComponent(numero)}/${encodeURIComponent(versao)}`,
      { method: 'POST', form, timeout: 120_000 },
    )
  },

  retifRegistrar: (numero: string, versao: string, justificativa: string, totalItens: number) => {
    const form = new FormData()
    form.append('justificativa', justificativa)
    form.append('total_itens', String(totalItens))
    return request(
      `/api/duimp/retificacao/registrar/${encodeURIComponent(numero)}/${encodeURIComponent(versao)}`,
      { method: 'POST', form, timeout: 120_000 },
    )
  },

  diagnosticoResultado: (numero: string, versao: string) =>
    request(
      `/api/duimp/diagnostico/${encodeURIComponent(numero)}/${encodeURIComponent(versao)}`,
      { timeout: 120_000 },
    ),

  /* ==================================================== configurações */

  configGet: () => request('/api/config/'),

  configSalvar: (d: Record<string, unknown>) =>
    request('/api/config/', { method: 'PUT', body: d, timeout: 60_000 }),

  certificadoEnviar: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/config/certificate', { method: 'POST', form, timeout: 60_000 })
  },

  certificadoSenha: (senha: string) =>
    request('/api/config/certificate/password', { method: 'POST', body: { password: senha } }),

  siscomexAutenticar: () =>
    request('/api/config/siscomex/authenticate', { method: 'POST', timeout: 120_000 }),

  siscomexStatusConexao: () => request('/api/config/siscomex/status'),

  smtpConfigGet: () => request('/api/processos/smtp/config'),

  smtpConfigSalvar: (d: {
    smtp_host: string
    smtp_port: number
    smtp_user: string
    smtp_password: string
    smtp_from?: string
    smtp_tls: boolean
    email_destino: string
  }) => request('/api/processos/smtp/config', { method: 'POST', body: d }),

  smtpTestar: () => request('/api/processos/smtp/testar', { method: 'POST', timeout: 60_000 }),

  /* ============================================== IA (BHLogueirinha) */

  duimpAiChat: (
    payload: { message: string; conversation_history?: unknown[]; file_id?: string },
    onEvent: (ev: Record<string, unknown>) => void,
  ) => streamSse('/api/duimp/ai/chat', payload, onEvent),

  duimpAiUpload: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/duimp/ai/upload', { method: 'POST', form, timeout: 120_000 })
  },

  /* ================================================ integrações de processo */

  procConsultarCnpj: (cnpj: string) =>
    request('/api/processos/consultar-cnpj', {
      query: { cnpj: cnpj.replace(/\D/g, '') },
      timeout: 30_000,
    }),

  procCargaConhecimento: (numero: string) =>
    request('/api/processos/carga/conhecimento', {
      query: { numero },
      timeout: 120_000,
    }),

  procCeMercante: (numero: string) =>
    request('/api/processos/ce/consultar', { query: { numero }, timeout: 120_000 }),

  procOcrExtrair: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/processos/ocr/extrair', { method: 'POST', form, timeout: 120_000 })
  },

  duimpStatus: (numero: string) =>
    request(`/api/processos/duimp-status/${encodeURIComponent(numero)}`, { timeout: 120_000 }),

  dueStatus: (numero: string) =>
    request(`/api/processos/due-status/${encodeURIComponent(numero)}`, { timeout: 120_000 }),

  lpcoStatusVivo: (numero: string) =>
    request(`/api/processos/lpco-status/${encodeURIComponent(numero)}`, { timeout: 120_000 }),

  autoVincularDuimp: (processoId: number) =>
    request(`/api/processos/auto-vincular-duimp/${processoId}`, {
      method: 'POST',
      timeout: 120_000,
    }),

  /* ========================================================= processos */

  /** Traz a lista inteira: o backend pagina em no máximo 200 por vez. */
  processos: listarProcessos,

  /** Anos do módulo — o total precisa do mesmo recorte da lista. */
  processosAnos: (tipo?: string) =>
    request('/api/processos/anos', { query: { tipo } }).then(
      (r) => (r as { anos?: { ano: number; total: number }[] })?.anos ?? [],
    ),

  /** A lista completa já traz os campos de vencimento. */
  processosVencimentos: () => listarProcessos(),

  processoDetalhe: (id: number) => request(`/api/processos/${id}`),

  processoSalvar: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/processos/${id}`, { method: 'PUT', body: corpo })
      : request('/api/processos/', { method: 'POST', body: corpo }),

  processoProcuracoes: (processoId: number) =>
    request(`/api/processos/procuracoes/${processoId}`),

  /** Alertas do processo. O backend só lista todos; o filtro é por processo aqui. */
  processoAlertas: async (processoId: number) => {
    const todos = await request<Array<{ processo_id?: number }>>('/api/processos/alertas/todos')
    return (todos ?? []).filter((a) => a.processo_id === processoId)
  },

  todosAlertas: () => request('/api/processos/alertas/todos'),

  processoRastreamento: (processoId: number) =>
    request(`/api/processos/rastreamento/${processoId}`),

  processoHistorico: (processoId: number) => request(`/api/processos/historico/${processoId}`),

  /** Resumo financeiro do processo: numerários, lançamentos e resultado. */
  processoFinanceiro: (processoId: number) =>
    request(`/api/financeiro/processos/${processoId}/resumo`),

  processoDocumentos: (processoId: number) => request(`/api/processos/documentos/${processoId}`),

  /** Upload é um arquivo por requisição; a tela manda a lista e aqui vira uma fila. */
  documentoProcessoSalvar: async (
    processoId: number,
    arquivos: Array<{ arquivo: File; tipo: string }>,
  ) => {
    const enviados: unknown[] = []
    for (const a of arquivos) {
      const form = new FormData()
      form.append('file', a.arquivo)
      form.append('tipo_documento', a.tipo)
      enviados.push(
        await request(`/api/processos/documentos/upload/${processoId}`, { method: 'POST', form }),
      )
    }
    return { enviados: enviados.length }
  },

  documentoProcessoRemover: (id: number) =>
    request(`/api/processos/documentos/excluir/${id}`, { method: 'DELETE' }),

  procuracaoSalvar: (processoId: number, dados: unknown) => {
    const d = dados as { id?: number }
    return d.id
      ? request(`/api/processos/procuracoes/editar/${d.id}`, { method: 'PUT', body: dados })
      : request('/api/processos/procuracoes', {
          method: 'POST',
          body: { ...(dados as object), processo_id: processoId },
        })
  },

  procuracaoRemover: (id: number) =>
    request(`/api/processos/procuracoes/excluir/${id}`, { method: 'DELETE' }),

  rastreamentoSalvar: (processoId: number, dados: unknown) =>
    request(`/api/processos/rastreamento/${processoId}`, { method: 'POST', body: dados }),

  /**
   * ⚠️ **ENDPOINT QUE AINDA NÃO EXISTE.** Único caso no arquivo — os outros 23
   * conferidos contra `_MAPA_API.md`.
   *
   * O envio de aviso de vencimento por e-mail está previsto na tela, mas o
   * backend não tem a rota. Enquanto não tiver, o botão devolve 404 com mensagem
   * legível (o `request` traduz), o que é melhor que esconder o botão: deixa
   * visível que a função existe no desenho e falta implementar.
   *
   * Ver item 1 de `REDESIGN_BACKEND_PENDENTE.md`.
   */
  alertasNotificar: (_ids: number[]) =>
    request('/api/processos/smtp/enviar-alertas', { method: 'POST', body: {} }),

  /* ========================================================= empresas */

  /** Lista completa de empresas (todas as páginas). */
  empresas: (papel?: string, incluirInativas?: boolean) =>
    todasPaginas<unknown>(async (page) => {
      const r = await request<{ total?: number; empresas?: unknown[] }>('/api/empresas/listar', {
        query: { papel, per_page: POR_PAGINA, page, incluir_inativas: incluirInativas ? 1 : 0 },
      })
      return { itens: r?.empresas ?? [], total: r?.total }
    }),

  empresaDetalhe: (id: number) =>
    request(`/api/empresas/detalhe/${id}`).then(
      (r) => (r as { empresa?: unknown })?.empresa ?? r,
    ),

  empresaSalvar: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/empresas/editar/${id}`, { method: 'PUT', body: corpo })
      : request('/api/empresas/criar', { method: 'POST', body: corpo }),

  empresaAtivo: (id: number) => request(`/api/empresas/ativo/${id}`, { method: 'PUT' }),

  /** Exclusão definitiva. O backend recusa (409) se houver processo vinculado. */
  empresaExcluir: (id: number) =>
    request(`/api/empresas/excluir/${id}`, { method: 'DELETE' }),

  /** Contas bancárias da BHLog cadastradas no Financeiro (fin_bancos). */
  bancos: () =>
    request('/api/financeiro/bancos').then(
      (r) => (r as { itens?: unknown[] })?.itens ?? r,
    ),

  consultaCnpj: (cnpj: string) =>
    request(`/api/empresas/consulta-cnpj/${cnpj.replace(/\D/g, '')}`),

  /* ========================================================= AWB */

  awbs: () => request('/api/awb').then((r) => (r as { itens?: unknown[] })?.itens ?? r),

  awbDetalhe: (id: number) => request(`/api/awb/${id}`),

  awbSalvar: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/awb/${id}`, { method: 'PUT', body: corpo })
      : request('/api/awb', { method: 'POST', body: corpo }),

  awbExcluir: (id: number) => request(`/api/awb/${id}`, { method: 'DELETE' }),

  awbPdf: (id: number, vias: string[], numero?: string) =>
    baixarArquivo(`/api/awb/${id}/pdf`, `awb-${numero ?? id}.pdf`, {
      method: 'POST',
      body: { vias },
    }),

  /* ========================================================= catálogo */

  catalogoClientes: () => request('/api/catalog/clientes'),

  /** 🐢 1 consulta ao SISCOMEX por cliente. Cache de 600 s no servidor. */
  catalogoClientesAcesso: () => request('/api/catalog/clientes/acesso', { timeout: 90_000 }),

  catalogoEmpresa: (cnpjRaiz?: string) =>
    request('/api/catalog/company-info', { query: { cnpj_raiz: cnpjRaiz } }),

  /**
   * Verificação de procuração antes de cadastrar o cliente.
   *
   * Não existe endpoint dedicado: usamos `company-info`, que já toca o catálogo do
   * CNPJ. Se responde, há procuração. A assinatura de "sem procuração" no Portal é
   * o 422 com `CATP-ER0003`.
   */
  catalogoVerificarProcuracao: async (cnpjInformado: string) => {
    const raiz = cnpjInformado.replace(/\D/g, '').slice(0, 8)
    try {
      const r = (await request('/api/catalog/company-info', {
        query: { cnpj_raiz: raiz },
      })) as { razao_social?: string; cnpj_raiz?: string }
      return {
        status: 'ok' as const,
        razao_social: r.razao_social,
        cnpj_raiz: r.cnpj_raiz ?? raiz,
      }
    } catch (e) {
      const msg = String((e as Error)?.message ?? '')
      if (msg.includes('CATP-ER0003') || msg.toLowerCase().includes('procura')) {
        return { status: 'sem_procuracao' as const, cnpj_raiz: raiz }
      }
      return { status: 'erro' as const, cnpj_raiz: raiz, detail: msg }
    }
  },

  catalogoCriarCliente: (razao: string, doc: string) =>
    request('/api/processos/clientes/criar', {
      method: 'POST',
      body: { razao_social: razao, cnpj: doc.replace(/\D/g, '') },
    }),

  catalogoRemoverCliente: (id: number) =>
    request(`/api/catalog/clientes/${id}`, { method: 'DELETE' }),

  catalogoProdutos: (cnpjRaiz?: string) =>
    request('/api/catalog/products', { query: { cnpj_raiz: cnpjRaiz }, timeout: 90_000 }),

  catalogoHistorico: () => request('/api/catalog/history'),

  /** Extrai itens de uma Invoice/Packing List (IA). Só leitura, não cadastra. */
  catalogoExtrairInvoice: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/catalog/ai/extrair-invoice', { method: 'POST', form, timeout: 120_000 })
  },

  /* ============================== Catálogo Inteligente (IA) */

  /** Sugere até 3 NCMs para uma descrição (IA). Revisão manual obrigatória. */
  catalogoSugerirNcm: (descricao: string) =>
    request('/api/duimp/ai/sugerir-ncm', {
      method: 'POST',
      body: { descricao },
      timeout: 90_000,
    }),

  /** Procura produtos duplicados no catálogo do cliente (NCM + similaridade). */
  catalogoVerificarDuplicados: (ncm: string, descricao: string, cnpjRaiz?: string) =>
    request('/api/catalog/ai/verificar-duplicados', {
      method: 'POST',
      body: { ncm, descricao },
      query: { cnpj_raiz: cnpjRaiz },
      timeout: 90_000,
    }),

  /** Pendências do catálogo: produtos não ativados e falhas de cadastro. */
  catalogoPendencias: (cnpjRaiz?: string) =>
    request('/api/catalog/pendencias', { query: { cnpj_raiz: cnpjRaiz }, timeout: 90_000 }),

  /**
   * Cadastra UM produto no CATP. O backend faz probe-and-retry: tenta criar,
   * o SISCOMEX devolve atributos obrigatórios/ inválidos e ele reenvia. Escrita
   * REAL no SISCOMEX do ambiente ativo.
   */
  catalogoCadastrarProduto: (d: {
    ncm: string
    descricao: string
    denominacao?: string
    modalidade?: string
    atributos?: unknown[]
    cnpjRaiz?: string
  }) => {
    const form = new FormData()
    form.append('ncm', d.ncm)
    form.append('descricao', d.descricao)
    if (d.denominacao) form.append('denominacao', d.denominacao)
    if (d.modalidade) form.append('modalidade', d.modalidade)
    if (d.atributos) form.append('atributos', JSON.stringify(d.atributos))
    return request('/api/catalog/register-single', {
      method: 'POST',
      form,
      query: { cnpj_raiz: d.cnpjRaiz },
      timeout: 180_000,
    })
  },

  /**
   * Extrai itens COMPLETOS de uma Invoice/PO/planilha para a DUIMP
   * (qtd, valor, peso, moeda, incoterm, país…). Só leitura, não grava nada.
   */
  duimpExtrairItens: (arquivo: File, cnpjImportador?: string) => {
    const form = new FormData()
    form.append('file', arquivo)
    // Com o CNPJ do importador o backend casa fabricante/exportador do
    // documento com o Catálogo real de operadores estrangeiros (CATP).
    if (cnpjImportador) form.append('cnpj', cnpjImportador)
    return request('/api/duimp/ai/extrair-itens', { method: 'POST', form, timeout: 300_000 })
  },

  catalogoSituacaoLote: () => request('/api/catalog/registration-status'),

  catalogoUpload: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/catalog/upload-spreadsheet', { method: 'POST', form, timeout: 120_000 })
  },

  /** 🐢 lê o catálogo inteiro do Portal para cruzar. */
  catalogoCruzar: (caminho: string, mapa: unknown) => {
    const form = new FormData()
    form.append('file_path', caminho)
    form.append('column_mapping', JSON.stringify(mapa))
    return request('/api/catalog/analyze-spreadsheet', { method: 'POST', form, timeout: 120_000 })
  },

  catalogoCadastrarOperadores: (operadores: unknown[]) => {
    const form = new FormData()
    form.append('operators_json', JSON.stringify(operadores))
    return request('/api/catalog/register-operators-batch', { method: 'POST', form })
  },

  catalogoCadastrarProdutos: (caminho: string, soNovos: boolean, mapa: unknown) => {
    const form = new FormData()
    form.append('file_path', caminho)
    form.append('column_mapping', JSON.stringify(mapa ?? {}))
    form.append('only_new', String(soNovos))
    return request('/api/catalog/register-products-batch', { method: 'POST', form })
  },

  /* ========================================================= LPCO */

  lpcoModelo: (codigo: string) =>
    request(`/api/lpco/modelo/${encodeURIComponent(codigo)}`, { timeout: 120_000 }),

  /**
   * Modelos de LPCO aplicáveis a uma NCM, direto do SISCOMEX (tratamento
   * administrativo). É assim que o Portal decide quais modelos valem: dependem da
   * NCM, do país e da condição da mercadoria — e do ambiente autenticado
   * (validação/produção). Nada hardcoded; a lista muda com o ambiente.
   */
  lpcoModelosPorNcm: async (ncm: string, pais: string, condicao: string) => {
    const resp = (await request('/api/duimp/simular-ta', {
      method: 'POST',
      body: {
        ncm: ncm.replace(/\D/g, ''),
        pais: pais.trim().toUpperCase().slice(0, 2),
        condicaoMercadoria: condicao,
      },
      timeout: 120_000,
    })) as { success?: boolean; data?: unknown; error?: unknown }
    if (!resp?.success) {
      const err = resp?.error
      throw new Error(
        typeof err === 'string' && err.trim()
          ? err
          : 'O SISCOMEX não retornou o tratamento administrativo desta NCM. Confira NCM, país e condição.',
      )
    }
    const tratamentos = Array.isArray(resp.data) ? (resp.data as Record<string, unknown>[]) : []
    const modelos: Array<{ codigo: string; nome: string; orgao: string; tipoTratamento: string }> =
      []
    const vistos = new Set<string>()
    for (const t of tratamentos) {
      const orgao = String(t.orgao ?? '').trim()
      const tipoTratamento = String(t.tipoTratamento ?? '').trim()
      const lista = Array.isArray(t.modelos) ? (t.modelos as Record<string, unknown>[]) : []
      for (const m of lista) {
        const codigo = String(m.codigo ?? '').trim().toUpperCase()
        if (!codigo || vistos.has(codigo)) continue
        vistos.add(codigo)
        modelos.push({ codigo, nome: String(m.nome ?? '').trim(), orgao, tipoTratamento })
      }
    }
    const info = tratamentos.map((t) => ({
      orgao: String(t.orgao ?? '').trim(),
      tipoTratamento: String(t.tipoTratamento ?? '').trim(),
      mensagem: String(t.mensagem ?? '').trim(),
    }))
    return { modelos, tratamentos: info }
  },

  lpcoDetalhe: (numero: string) => request(`/api/lpco/detail/${encodeURIComponent(numero)}`),

  /**
   * Palavras-chave exigidas por um tipo de documento — puxadas do SISCOMEX
   * (`/edocex/api/ext/tipos-documentos/{id}/palavras-chave`). Cada palavra tem o
   * seu `id` real (idPalavraChave), nome e se é obrigatória. Nada de id fixo.
   */
  documentoPalavrasChave: (idTipo: string | number) =>
    request(
      `/api/duimp/referencia/documento-palavras-chave/${encodeURIComponent(idTipo)}`,
      { timeout: 60_000 },
    ),

  lpcoAnexarDocumentos: (
    numero: string,
    arquivos: Array<{
      arquivo: File
      docTypeId: string
      docTypeNome: string
      /** Conteúdo por palavra-chave, indexado pelo idPalavraChave real do SISCOMEX. */
      palavrasChave: Record<string, string>
    }>,
  ) => {
    const form = new FormData()
    form.append(
      'doc_types',
      JSON.stringify(arquivos.map((a) => ({ id: Number(a.docTypeId), name: a.docTypeNome }))),
    )
    form.append(
      'keywords_list',
      JSON.stringify(
        arquivos.map((a) =>
          Object.entries(a.palavrasChave)
            .filter(([, v]) => v.trim() !== '')
            .map(([id, conteudo]) => ({ idPalavraChave: Number(id), conteudo: conteudo.trim() })),
        ),
      ),
    )
    for (const a of arquivos) form.append('files', a.arquivo)
    return request(`/api/lpco/attach-documents/${encodeURIComponent(numero)}`, {
      method: 'POST',
      form,
      timeout: 120_000,
    })
  },

  lpcoCriar: (codigo: string, valoresBrutos: unknown, ncmsBrutos: unknown[]) => {
    const valores = (valoresBrutos ?? {}) as Record<string, string[]>
    const campos_formulario = Object.entries(valores)
      .filter(([, v]) => v?.length && v.some((x) => String(x).trim() !== ''))
      .map(([codigo_campo, listaValor]) => ({ codigo: codigo_campo, listaValor }))
    const lista_ncm = ((ncmsBrutos ?? []) as Array<{ ncm: string; valores?: Record<string, string[]> }>)
      .filter((n) => n.ncm)
      .map((n) => ({
        codigoNcm: n.ncm,
        listaCamposNcm: Object.entries(n.valores ?? {})
          .filter(([, v]) => v?.length && v.some((x) => String(x).trim() !== ''))
          .map(([codigo_campo, listaValor]) => ({ codigo: codigo_campo, listaValor })),
      }))
    return request('/api/lpco/create', {
      method: 'POST',
      body: { modelo_lpco: codigo, campos_formulario, lista_ncm },
      timeout: 90_000,
    })
  },


  /** A lista vem do histórico: o vínculo com processo está vazio (B-40). */
  lpcoPainel: () => listarHistorico('/api/history/lpcos'),

  /* ========================================================= histórico LPCO */

  historicoLpcos: () => listarHistorico('/api/history/lpcos'),

  historicoDocumentos: () => listarHistorico('/api/history/documents'),

  historicoSalvarProcesso: (id: number, numero: string) =>
    request(`/api/history/lpcos/${id}/processo`, {
      method: 'PUT',
      body: { numero_processo: numero },
    }),

  historyDashboard: () => request('/api/history/dashboard'),

  historyChart: (dias: number) => request('/api/history/chart-data', { query: { days: dias } }),

  /* ========================================================= mapeamento */

  mapeamentos: () => request('/api/mappings/'),

  tiposDocumento: async () => {
    const bruto = (await request('/api/lpco/document-types')) as unknown
    const lista = Array.isArray(bruto) ? (bruto as Record<string, unknown>[]) : []
    return lista
      .map((t) => ({
        id: Number(t.idTipoDocumento ?? t.id ?? t.codigo ?? t.codigoTipoDocumento),
        name: String(
          t.nomeTipoDocumento ?? t.nome ?? t.name ?? t.descricao ?? t.descricaoTipoDocumento ?? '',
        ).trim(),
      }))
      .filter((t) => Number.isFinite(t.id) && t.name.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  },

  mapeamentoSalvar: (dados: unknown) => {
    const d = dados as { id?: number }
    return d.id
      ? request(`/api/mappings/${d.id}`, { method: 'PUT', body: dados })
      : request('/api/mappings/', { method: 'POST', body: dados })
  },

  mapeamentoToggle: (id: number) => request(`/api/mappings/${id}/toggle`, { method: 'PATCH' }),

  mapeamentoRemover: (id: number) => request(`/api/mappings/${id}`, { method: 'DELETE' }),

  /* ========================================================= envio em lote */

  loteUpload: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/batch/upload-spreadsheet', { method: 'POST', form, timeout: 120_000 })
  },

  /**
   * As três listas — tipos, palavras-chave e arquivos — vão na **mesma ordem**.
   * O backend casa por índice, não por nome do arquivo.
   */
  loteIniciar: (
    caminho: string,
    mapa: unknown,
    arquivos: Array<{
      arquivo: File
      docTypeId: string
      docTypeNome?: string | null
      palavrasChave: Record<string, string>
    }>,
  ) => {
    const form = new FormData()
    form.append('spreadsheet_path', caminho)
    form.append('column_mapping', JSON.stringify(mapa))
    form.append(
      'doc_types',
      JSON.stringify(
        arquivos.map((a) => ({ id: Number(a.docTypeId), name: a.docTypeNome ?? '' })),
      ),
    )
    form.append(
      'keywords_list',
      JSON.stringify(
        arquivos.map((a) =>
          Object.entries(a.palavrasChave)
            .filter(([, v]) => v.trim() !== '')
            .map(([id, conteudo]) => ({ idPalavraChave: Number(id), conteudo: conteudo.trim() })),
        ),
      ),
    )
    for (const a of arquivos) form.append('files', a.arquivo)
    return request('/api/batch/start', { method: 'POST', form, timeout: 120_000 })
  },

  loteSituacao: () => request('/api/batch/current-status'),

  loteHistorico: () => request('/api/batch/jobs'),

  /* ============================================================== perfil */

  perfilGet: () => request('/api/auth/me'),

  perfilSalvar: (corpo: {
    name?: string
    telefone?: string
    ramal?: string
    celular?: string
    email_contato?: string
  }) => request('/api/auth/perfil', { method: 'PUT', body: corpo }),

  /* ========================================================= financeiro */

  estimativas: () =>
    request('/api/financeiro/estimativas').then(
      (r) => (r as { itens?: unknown[] })?.itens ?? r,
    ),

  estimativaDetalhe: (id: number) => request(`/api/financeiro/estimativas/${id}`),

  estimativaSalvar: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/financeiro/estimativas/${id}`, { method: 'PUT', body: corpo })
      : request('/api/financeiro/estimativas', { method: 'POST', body: corpo }),

  estimativaPdf: (id: number, numero?: string) =>
    baixarArquivo(
      `/api/financeiro/estimativas/${id}/pdf`,
      `estimativa-${(numero ?? String(id)).replace('/', '-')}.pdf`,
    ),

  estimativaExcluir: (id: number) =>
    request(`/api/financeiro/estimativas/${id}`, { method: 'DELETE' }),

  estimativaImportarPo: (arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request('/api/financeiro/estimativas/importar-po', {
      method: 'POST',
      form,
      timeout: 120_000,
    })
  },

  estimativaAliquotasNcm: (ncm: string) =>
    request(`/api/financeiro/estimativas/aliquotas-ncm/${ncm}`, {
      timeout: 90_000,
    }),

  /** Catálogo de custos fixos nacionais por modal (reutilizáveis na estimativa). */
  custosFixos: (modal: string) =>
    request('/api/financeiro/custos-fixos', { query: { modal } }).then(
      (r) => (r as { itens?: unknown[] })?.itens ?? r,
    ),

  custoFixoSalvar: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/financeiro/custos-fixos/${id}`, { method: 'PUT', body: corpo })
      : request('/api/financeiro/custos-fixos', { method: 'POST', body: corpo }),

  custoFixoExcluir: (id: number) =>
    request(`/api/financeiro/custos-fixos/${id}`, { method: 'DELETE' }),

  ptaxCotacao: (moeda = 'USD', data?: string) =>
    request('/api/processos/ptax/cotacao', { query: { moeda, data } }),

  finListar: (recurso: string, incluirInativos: boolean) =>
    request(`/api/financeiro/${finRota(recurso)}`, {
      query: { incluir_inativos: incluirInativos },
    }),

  finSalvar: (recurso: string, id: number | undefined, dados: unknown) =>
    id
      ? request(`/api/financeiro/${finRota(recurso)}/${id}`, { method: 'PUT', body: dados })
      : request(`/api/financeiro/${finRota(recurso)}`, { method: 'POST', body: dados }),

  finAlternarAtivo: (recurso: string, id: number) =>
    request(`/api/financeiro/${finRota(recurso)}/${id}/ativo`, { method: 'POST' }),

  finLancamentos: (f: {
    tipo?: string
    status?: string
    processo_id?: number
    classificacao?: string
    processo_ref?: string
  }) => request('/api/financeiro/lancamentos', { query: f }),

  /** Busca de processos para vincular o lançamento — qualquer módulo. */
  finBuscarProcessos: (q: string) =>
    request('/api/processos/buscar-numero', { query: { q } }) as Promise<
      Array<{ id: number; numero_processo: string; tipo?: string; cliente?: string }>
    >,

  finEnviarNf: (id: number, arquivo: File) => {
    const form = new FormData()
    form.append('file', arquivo)
    return request(`/api/financeiro/lancamentos/${id}/nf-arquivo`, {
      method: 'POST',
      form,
      timeout: 120_000,
    })
  },

  finBaixarNf: (id: number, nome?: string) =>
    baixarArquivo(`/api/financeiro/lancamentos/${id}/nf-arquivo`, nome || `nota-fiscal-${id}.pdf`),

  finExcluirNf: (id: number) =>
    request(`/api/financeiro/lancamentos/${id}/nf-arquivo`, { method: 'DELETE' }),

  finSalvarLancamento: (id: number | undefined, dados: unknown) =>
    id
      ? request(`/api/financeiro/lancamentos/${id}`, { method: 'PUT', body: dados })
      : request('/api/financeiro/lancamentos', { method: 'POST', body: dados }),

  finStatusLancamento: (id: number, status: string) =>
    request(`/api/financeiro/lancamentos/${id}/status`, { method: 'POST', body: { status } }),

  finExcluirLancamento: (id: number) =>
    request(`/api/financeiro/lancamentos/${id}`, { method: 'DELETE' }),

  /**
   * Consulta a DUIMP no Portal antes de criar os lançamentos, então é lento.
   * `idempotencyKey` protege contra duplo clique — e o backend também deduplica
   * por descrição, o que dá duas barreiras.
   */
  finImportarDuimp: (numero: string, processoRef: string) =>
    request('/api/financeiro/lancamentos/importar-duimp', {
      method: 'POST',
      body: { numero_duimp: numero, processo_ref: processoRef },
      timeout: 90_000,
      idempotencyKey: `importar-duimp:${numero}`,
    }),

  finCreditosClientes: () => request('/api/financeiro/creditos-clientes'),

  finVincularProcesso: (id: number, corpo: { processo_id?: number; processo_ref?: string }) =>
    request(`/api/financeiro/lancamentos/${id}/vincular-processo`, {
      method: 'POST',
      body: corpo,
    }),

  finFluxoNumerario: (
    id: number,
    acao: 'enviar' | 'aprovar' | 'recusar' | 'receber',
    corpo?: Record<string, unknown>,
  ) => request(`/api/financeiro/numerarios/${id}/${acao}`, { method: 'POST', body: corpo ?? {} }),

  finHistoricoNumerario: (id: number) => request(`/api/financeiro/numerarios/${id}/historico`),

  finContasPadrao: (ano?: number) =>
    request('/api/financeiro/contas-padrao', { query: { ano } }),

  finSalvarContaPadrao: (
    id: number | undefined,
    corpo: Record<string, unknown>,
  ) =>
    request(id ? `/api/financeiro/contas-padrao/${id}` : '/api/financeiro/contas-padrao', {
      method: id ? 'PUT' : 'POST',
      body: corpo,
    }),

  finExcluirContaPadrao: (id: number, apagarParcelas = false) =>
    request(`/api/financeiro/contas-padrao/${id}`, {
      method: 'DELETE',
      query: { apagar_parcelas: apagarParcelas },
    }),

  finGerarParcelas: (
    id: number,
    corpo: { ano: number; mes_inicio?: number; mes_fim?: number },
  ) =>
    request(`/api/financeiro/contas-padrao/${id}/gerar`, { method: 'POST', body: corpo }),

  finParcelasContaPadrao: (id: number, ano?: number) =>
    request(`/api/financeiro/contas-padrao/${id}/parcelas`, { query: { ano } }),

  finSalvarParcela: (
    contaId: number,
    lanId: number,
    corpo: { valor?: number; vencimento?: string; descricao?: string },
  ) =>
    request(`/api/financeiro/contas-padrao/${contaId}/parcelas/${lanId}`, {
      method: 'PUT',
      body: corpo,
    }),

  finExcluirParcela: (contaId: number, lanId: number) =>
    request(`/api/financeiro/contas-padrao/${contaId}/parcelas/${lanId}`, { method: 'DELETE' }),

  finSaldosBancos: () => request('/api/financeiro/saldos-bancos'),

  finTransferencias: () => request('/api/financeiro/transferencias'),

  finTransferir: (corpo: {
    banco_origem_id: number
    banco_destino_id: number
    valor: number
    data?: string
    observacoes?: string
  }) => request('/api/financeiro/transferencias', { method: 'POST', body: corpo }),

  finExcluirTransferencia: (id: string) =>
    request(`/api/financeiro/transferencias/${id}`, { method: 'DELETE' }),

  finConciliar: (id: number, conciliado: boolean, extratoRef?: string) =>
    request(`/api/financeiro/lancamentos/${id}/conciliar`, {
      method: 'POST',
      body: { conciliado, extrato_ref: extratoRef },
    }),

  finConciliacao: (f: { banco_id: number; mes?: string; saldo_extrato?: number }) =>
    request('/api/financeiro/conciliacao', { query: f }),

  finBaixarProfit: (id: number) =>
    request(`/api/financeiro/lancamentos/${id}/baixar-profit`, { method: 'POST' }),

  finContas: (visao: string) => request('/api/financeiro/contas', { query: { visao } }),

  finFechamento: (processoRef: string) =>
    request('/api/financeiro/fechamento', { query: { processo_ref: processoRef } }),

  finFluxoCaixa: (meses: number) =>
    request('/api/financeiro/fluxo-caixa', { query: { meses } }),

  finKpis: () => request('/api/financeiro/kpis'),

  finNumerarios: () => request('/api/financeiro/numerarios'),

  finNumerario: (id: number) => request(`/api/financeiro/numerarios/${id}`),

  finSalvarNumerario: (id: number | undefined, dados: unknown) =>
    id
      ? request(`/api/financeiro/numerarios/${id}`, { method: 'PUT', body: dados })
      : request('/api/financeiro/numerarios', { method: 'POST', body: dados }),

  finExcluirNumerario: (id: number) =>
    request(`/api/financeiro/numerarios/${id}`, { method: 'DELETE' }),

  /* ==================================================== faturamento */

  finFaturas: (filtros?: { processo_id?: number; status?: string }) =>
    request('/api/financeiro/faturas', { query: filtros ?? {} }),

  finFatura: (id: number) => request(`/api/financeiro/faturas/${id}`),

  finSalvarFatura: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/financeiro/faturas/${id}`, { method: 'PUT', body: corpo })
      : request('/api/financeiro/faturas', { method: 'POST', body: corpo }),

  finExcluirFatura: (id: number) =>
    request(`/api/financeiro/faturas/${id}`, { method: 'DELETE' }),

  finFluxoFatura: (id: number, acao: string, corpo?: unknown) =>
    request(`/api/financeiro/faturas/${id}/${acao}`, { method: 'POST', body: corpo ?? {} }),

  finPdfNumerario: (id: number, numero?: string) =>
    baixarArquivo(
      `/api/financeiro/numerarios/${id}/pdf`,
      `numerario-${(numero ?? String(id)).replace(/\//g, '-')}.pdf`,
    ),

  finPdfFatura: (id: number, numero?: string) =>
    baixarArquivo(
      `/api/financeiro/faturas/${id}/pdf`,
      `fatura-${(numero ?? String(id)).replace(/\//g, '-')}.pdf`,
    ),

  /** Fechamento do processo: consolidado e itens sugeridos para a fatura. */
  finFechamentoProcesso: (processoId: number) =>
    request(`/api/financeiro/processos/${processoId}/fechamento`),


  /* ========================= conta-corrente do processo */

  /** Numerario, consumo, saldo e falta de cada processo, para a lista geral. */
  finResumoProcessos: (f?: { q?: string; tipo?: string; situacao?: string; status?: string }) =>
    request('/api/financeiro/processos/resumo', { query: f }),

  /** Credito (numerario), consumo, saldo e extrato de um processo. */
  finContaCorrente: (processoId: number) =>
    request(`/api/financeiro/processos/${processoId}/conta-corrente`),

  finAbaterCredito: (lancamentoId: number) =>
    request(`/api/financeiro/lancamentos/${lancamentoId}/abater-credito`, { method: 'POST' }),

  finEstornarAbatimento: (lancamentoId: number) =>
    request(`/api/financeiro/lancamentos/${lancamentoId}/estornar-abatimento`, {
      method: 'POST',
    }),

  /** Abate de uma vez o que couber no saldo; devolve o que faltou numerario. */
  finAbaterCreditoLote: (processoId: number, lancamentos?: number[]) =>
    request(`/api/financeiro/processos/${processoId}/abater-credito`, {
      method: 'POST',
      body: { lancamentos },
    }),

  /* ====================== caixa de entrada de despesas */

  finPendencias: (filtros?: { status?: string; processo_id?: number }) =>
    request('/api/financeiro/despesas-pendentes', { query: filtros ?? {} }),

  finEnviarPendencia: (campos: Record<string, string | number | undefined>, arquivo?: File) => {
    const form = new FormData()
    for (const [k, v] of Object.entries(campos)) {
      if (v !== undefined && v !== null && v !== '') form.append(k, String(v))
    }
    if (arquivo) form.append('file', arquivo)
    return request('/api/financeiro/despesas-pendentes', { method: 'POST', form })
  },

  finArquivoPendencia: (id: number, nome?: string) =>
    baixarArquivo(`/api/financeiro/despesas-pendentes/${id}/arquivo`, nome ?? `despesa-${id}`),

  finLancarPendencia: (id: number, corpo: unknown) =>
    request(`/api/financeiro/despesas-pendentes/${id}/lancar`, { method: 'POST', body: corpo }),

  finRecusarPendencia: (id: number, motivo: string) =>
    request(`/api/financeiro/despesas-pendentes/${id}/recusar`, {
      method: 'POST',
      body: { motivo },
    }),

  /* ================= administrativo: funcionarios e folha */

  finFuncionarios: (incluirInativos = false) =>
    request('/api/financeiro/funcionarios', { query: { incluir_inativos: incluirInativos } }),

  finSalvarFuncionario: (id: number | undefined, corpo: unknown) =>
    id
      ? request(`/api/financeiro/funcionarios/${id}`, { method: 'PUT', body: corpo })
      : request('/api/financeiro/funcionarios', { method: 'POST', body: corpo }),

  finExcluirFuncionario: (id: number) =>
    request(`/api/financeiro/funcionarios/${id}`, { method: 'DELETE' }),

  finFolha: (competencia: string) => request(`/api/financeiro/folha/${competencia}`),

  finGerarFolha: (corpo: unknown) =>
    request('/api/financeiro/folha/gerar', { method: 'POST', body: corpo }),

  /* ============================ centro de custo */

  finRatearCentros: (lancamentoId: number, partes: unknown) =>
    request(`/api/financeiro/lancamentos/${lancamentoId}/ratear-centros`, {
      method: 'POST',
      body: { partes },
    }),

  finRelatorioCentros: (filtros?: { de?: string; ate?: string; classificacao?: string }) =>
    request('/api/financeiro/centros-custo/relatorio', { query: filtros ?? {} }),
  /* ========================================================= admin */

  usuarios: () => request('/api/admin/users'),

  adminFeatures: () => request('/api/admin/features'),

  adminAlternarFeature: (feature: string) =>
    request(`/api/admin/features/${encodeURIComponent(feature)}`, { method: 'PUT' }),
}
